import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Droplets, Phone, Mail, MapPin, CheckCircle2, Send } from 'lucide-react';
import axios from 'axios';
import AddressAutocomplete from '@/components/common/AddressAutocomplete';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// NOTE (client-facing English only): this public "Request Service" form is
// filled out by a potential CUSTOMER, never an internal CRM user — its copy
// must NEVER depend on the admin's language toggle (es/en) or browser
// locale. A local hardcoded dictionary (instead of react-i18next's
// useTranslation) guarantees English always, regardless of localStorage.
const EN = {
  publicBooking: {
    title: 'Request Service',
    subtitle: 'Complete the form and we will contact you within 24 hours',
    personalInfo: 'Personal Information',
    serviceType: 'Service Type',
    submitRequest: 'Submit Request',
    requestSent: 'Request Received',
    weWillContact: 'Your request has been successfully registered.',
    contactSoon: 'We will contact you as soon as possible to coordinate the service.',
    firstName: 'First Name', lastName: 'Last Name', phone: 'Phone', email: 'Email',
    emailPlaceholder: 'email@example.com',
    address: 'Address', addressPlaceholder: '123 Main St',
    city: 'City', cityPlaceholder: 'Sebring',
    county: 'County', countySelect: 'Select',
    zipCode: 'Zip Code', zipPlaceholder: '33870',
    description: 'Service Description', descriptionPlaceholder: 'Briefly describe what you need...',
    howHeard: 'How did you find us?', howHeardSelect: 'Select',
    preferredDate: 'Preferred Date', required: '*',
    nameRequired: 'Name and phone are required',
    errorSubmitting: 'Error submitting request. Please try again.',
    referenceNumber: 'Reference number',
    submitAnother: 'Submit another request',
    services: {
      new_well: 'New Well Drilling', repair: 'Well Repair', pump_service: 'Pump Service',
      modification: 'Well Modification', abandonment: 'Well Abandonment',
    },
    sources: {
      google: 'Google', referral: 'Referral', social_media: 'Social Media', sign: 'Sign/Vehicle', other: 'Other',
    },
    companyName: 'Blue Well Drilling', companyLocation: 'Sebring, Florida',
    companyPhone: '(863) 658-0035', companyEmail: 'info@bluewellfl.com',
    companyFooter: 'Blue Well Drilling LLC | Sebring, FL | (863) 658-0035',
    serviceRequired: 'Service Required', propertyLocation: 'Property Location',
    yourName: 'Your name', yourLastName: 'Your last name',
  },
};
const t = (key) => key.split('.').reduce((o, k) => (o ? o[k] : undefined), EN) ?? key;

// Format phone number as (XXX) XXX-XXXX
const formatPhoneNumber = (value) => {
  const digits = value.replace(/\D/g, '');
  const limited = digits.slice(0, 10);
  if (limited.length === 0) return '';
  if (limited.length <= 3) return `(${limited}`;
  if (limited.length <= 6) return `(${limited.slice(0, 3)}) ${limited.slice(3)}`;
  return `(${limited.slice(0, 3)}) ${limited.slice(3, 6)}-${limited.slice(6)}`;
};

const COUNTIES = [
  "Highlands", "Polk", "Osceola", "Hardee", "DeSoto", "Okeechobee",
  "Glades", "Charlotte", "Lee", "Sarasota", "Manatee", "Hillsborough",
  "Orange", "Seminole", "Brevard", "Indian River", "St. Lucie", "Palm Beach",
  "Miami-Dade", "Broward", "Monroe"
];

export default function PublicBookingPage() {
  const EMPTY_FORM = {
    first_name: '', last_name: '', email: '', phone: '',
    service_type: 'new_construction', address: '', city: '', county: '', zip_code: '',
    description: '', preferred_date: '', preferred_day: 'asap', how_heard: ''
  };
  const [submitted, setSubmitted] = useState(false);
  const [bookingNumber, setBookingNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState(EMPTY_FORM);
  // D2 (Alian): let the same person submit multiple requests without
  // refreshing the page — bumping this key forces the address autocomplete
  // to fully remount and drop any stale "verified" state from the prior submit.
  const [formKey, setFormKey] = useState(0);

  const resetForFreshSubmission = () => {
    setForm(EMPTY_FORM);
    setError('');
    setBookingNumber('');
    setSubmitted(false);
    setFormKey((k) => k + 1);
  };

  const SERVICE_TYPES = [
    { value: 'new_construction', label: t('publicBooking.services.new_well') },
    { value: 'repair', label: t('publicBooking.services.repair') },
    { value: 'pump_service', label: t('publicBooking.services.pump_service') },
    { value: 'modification', label: t('publicBooking.services.modification') },
    { value: 'abandonment', label: t('publicBooking.services.abandonment') },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.phone) {
      setError(t('publicBooking.nameRequired'));
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API_URL}/bookings/public`, form);
      setBookingNumber(res.data.booking_number);
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || t('publicBooking.errorSubmitting'));
    } finally { setLoading(false); }
  };

  if (submitted) {
    return (
      <div data-testid="booking-success" className="min-h-screen bg-gradient-to-br from-[#0A1628] via-[#0F1D32] to-[#0A1628] flex items-center justify-center p-4">
        <Card className="max-w-lg w-full bg-white/[0.03] border border-white/[0.08] backdrop-blur-xl shadow-2xl">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-8 h-8 text-emerald-400" />
            </div>
            <h2 className="text-2xl font-bold text-white font-['Barlow'] mb-2">{t('publicBooking.requestSent')}</h2>
            <p className="text-slate-400 mb-4">{t('publicBooking.weWillContact')}</p>
            <div className="bg-white/[0.05] rounded-lg p-4 mb-6">
              <p className="text-sm text-slate-400">{t('publicBooking.referenceNumber')}</p>
              <p className="text-xl font-bold text-[#3399FF] font-mono">{bookingNumber}</p>
            </div>
            <p className="text-sm text-slate-500">{t('publicBooking.contactSoon')}</p>
            <Button
              type="button"
              data-testid="booking-submit-another-btn"
              onClick={resetForFreshSubmission}
              variant="outline"
              className="mt-6 border-[#3399FF]/40 text-[#3399FF] hover:bg-[#3399FF]/10"
            >
              <Send className="w-4 h-4 mr-2" /> {t('publicBooking.submitAnother')}
            </Button>
            <div className="mt-6 flex items-center justify-center gap-4 text-sm text-slate-400">
              <a href={`tel:${t('publicBooking.companyPhone')}`} className="flex items-center gap-1 hover:text-[#3399FF] transition-colors">
                <Phone className="w-3.5 h-3.5" /> {t('publicBooking.companyPhone')}
              </a>
              <a href={`mailto:${t('publicBooking.companyEmail')}`} className="flex items-center gap-1 hover:text-[#3399FF] transition-colors">
                <Mail className="w-3.5 h-3.5" /> {t('publicBooking.companyEmail')}
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div data-testid="public-booking-page" className="min-h-screen bg-gradient-to-br from-[#0A1628] via-[#0F1D32] to-[#0A1628]">
      {/* Header */}
      <div className="border-b border-white/[0.06] bg-white/[0.02] backdrop-blur-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#003366] to-[#3399FF] flex items-center justify-center">
              <Droplets className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white font-['Barlow']">{t('publicBooking.companyName')}</h1>
              <p className="text-xs text-slate-500">{t('publicBooking.companyLocation')}</p>
            </div>
          </div>
          <a href={`tel:${t('publicBooking.companyPhone')}`} className="text-sm text-slate-400 hover:text-[#3399FF] flex items-center gap-1.5 transition-colors">
            <Phone className="w-3.5 h-3.5" /> {t('publicBooking.companyPhone')}
          </a>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8 sm:py-12">
        <div className="text-center mb-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-white font-['Barlow'] tracking-tight">
            {t('publicBooking.title')}
          </h2>
          <p className="text-slate-400 mt-2 text-base">{t('publicBooking.subtitle')}</p>
        </div>

        <Card className="bg-white/[0.03] border border-white/[0.08] backdrop-blur-xl shadow-2xl">
          <CardContent className="p-6 sm:p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">{error}</div>
              )}

              {/* Personal Info */}
              <div>
                <p className="text-sm font-semibold text-[#3399FF] uppercase tracking-wider mb-4">{t('publicBooking.personalInfo')}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">{t('publicBooking.firstName')} {t('publicBooking.required')}</Label>
                    <Input data-testid="booking-first-name" value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} placeholder={t('publicBooking.yourName')} className="bg-white/[0.05] border-white/[0.1] text-white placeholder:text-slate-500" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">{t('publicBooking.lastName')} {t('publicBooking.required')}</Label>
                    <Input data-testid="booking-last-name" value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} placeholder={t('publicBooking.yourLastName')} className="bg-white/[0.05] border-white/[0.1] text-white placeholder:text-slate-500" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">{t('publicBooking.phone')} {t('publicBooking.required')}</Label>
                    <Input data-testid="booking-phone" value={form.phone} onChange={e => setForm({ ...form, phone: formatPhoneNumber(e.target.value) })} placeholder="(786) 510-4512" className="bg-white/[0.05] border-white/[0.1] text-white placeholder:text-slate-500" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">{t('publicBooking.email')}</Label>
                    <Input data-testid="booking-email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder={t('publicBooking.emailPlaceholder')} type="email" className="bg-white/[0.05] border-white/[0.1] text-white placeholder:text-slate-500" />
                  </div>
                </div>
              </div>

              {/* Service */}
              <div>
                <p className="text-sm font-semibold text-[#3399FF] uppercase tracking-wider mb-4">{t('publicBooking.serviceRequired')}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">{t('publicBooking.serviceType')}</Label>
                    <Select value={form.service_type} onValueChange={v => setForm({ ...form, service_type: v })}>
                      <SelectTrigger data-testid="booking-service-type" className="bg-white/[0.05] border-white/[0.1] text-white"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {SERVICE_TYPES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">{t('publicBooking.preferredDate')}</Label>
                    <div className="grid grid-cols-3 gap-2" data-testid="booking-preferred-day">
                      {[
                        { v: 'asap', label: 'ASAP', sub: '24h' },
                        { v: 'any_day', label: 'Any Day', sub: '48h' },
                        { v: 'specific', label: 'Pick Date', sub: '' },
                      ].map((o) => (
                        <button
                          key={o.v}
                          type="button"
                          data-testid={`booking-preferred-day-${o.v}`}
                          onClick={() => setForm({ ...form, preferred_day: o.v })}
                          className={`h-10 rounded-md border text-xs font-medium transition-all ${form.preferred_day === o.v ? 'border-[#3399FF] bg-[#3399FF]/20 text-white' : 'border-white/[0.1] bg-white/[0.05] text-slate-400 hover:border-white/25'}`}
                        >
                          {o.label}{o.sub ? <span className="block text-[9px] opacity-70">{o.sub}</span> : null}
                        </button>
                      ))}
                    </div>
                    {form.preferred_day === 'specific' && (
                      <Input data-testid="booking-date" type="date" value={form.preferred_date} onChange={e => setForm({ ...form, preferred_date: e.target.value })} className="bg-white/[0.05] border-white/[0.1] text-white mt-2" />
                    )}
                  </div>
                </div>
              </div>

              {/* Location */}
              <div>
                <p className="text-sm font-semibold text-[#3399FF] uppercase tracking-wider mb-4 flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5" /> {t('publicBooking.propertyLocation')}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2 space-y-2">
                    <Label className="text-slate-300">{t('publicBooking.address')}</Label>
                    <div className="[&_input]:bg-white/[0.05] [&_input]:border-white/[0.1] [&_input]:text-white [&_input]:placeholder:text-slate-500">
                      <AddressAutocomplete
                        key={formKey}
                        value={form.address}
                        onChange={(addressData) => {
                          const isFullSelection = addressData.lat !== null && addressData.lat !== undefined;
                          setForm(prev => ({
                            ...prev,
                            address: addressData.address || '',
                            city: isFullSelection ? (addressData.city || '') : (addressData.city || prev.city),
                            county: isFullSelection ? (addressData.county || '') : (addressData.county || prev.county),
                            zip_code: isFullSelection ? (addressData.zip_code || '') : (addressData.zip_code || prev.zip_code),
                          }));
                        }}
                        placeholder={t('publicBooking.addressPlaceholder')}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">{t('publicBooking.city')}</Label>
                    <Input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} placeholder={t('publicBooking.cityPlaceholder')} className="bg-white/[0.05] border-white/[0.1] text-white placeholder:text-slate-500" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">{t('publicBooking.county')}</Label>
                    <Select value={form.county} onValueChange={v => setForm({ ...form, county: v })}>
                      <SelectTrigger data-testid="booking-county" className="bg-white/[0.05] border-white/[0.1] text-white"><SelectValue placeholder={t('publicBooking.countySelect')} /></SelectTrigger>
                      <SelectContent>
                        {COUNTIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">{t('publicBooking.zipCode')}</Label>
                    <Input value={form.zip_code} onChange={e => setForm({ ...form, zip_code: e.target.value })} placeholder={t('publicBooking.zipPlaceholder')} className="bg-white/[0.05] border-white/[0.1] text-white placeholder:text-slate-500" />
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label className="text-slate-300">{t('publicBooking.description')}</Label>
                <Textarea data-testid="booking-description" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder={t('publicBooking.descriptionPlaceholder')} rows={3} className="bg-white/[0.05] border-white/[0.1] text-white placeholder:text-slate-500" />
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">{t('publicBooking.howHeard')}</Label>
                <Select value={form.how_heard} onValueChange={v => setForm({ ...form, how_heard: v })}>
                  <SelectTrigger className="bg-white/[0.05] border-white/[0.1] text-white"><SelectValue placeholder={t('publicBooking.howHeardSelect')} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="google">{t('publicBooking.sources.google')}</SelectItem>
                    <SelectItem value="referral">{t('publicBooking.sources.referral')}</SelectItem>
                    <SelectItem value="social_media">{t('publicBooking.sources.social_media')}</SelectItem>
                    <SelectItem value="sign">{t('publicBooking.sources.sign')}</SelectItem>
                    <SelectItem value="other">{t('publicBooking.sources.other')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button data-testid="submit-booking-btn" type="submit" disabled={loading} className="w-full h-12 bg-gradient-to-r from-[#003366] to-[#004080] hover:from-[#004080] hover:to-[#005099] text-white font-semibold text-base shadow-lg">
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <><Send className="w-4 h-4 mr-2" /> {t('publicBooking.submitRequest')}</>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-slate-600 mt-6">
          {t('publicBooking.companyFooter')}
        </p>
      </div>
    </div>
  );
}
